import axios from 'axios';
import {
  Category,
  CategoryFilters,
  CategoryPaginationParams,
  ApiResponse,
} from '../types/category';
import { PaginatedResponse, StringOption } from '../types/index';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

/**
 * Service class for Category operations
 * Supports both Mock data and real API calls
 */
export class CategoryService {
  private static instance: CategoryService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): CategoryService {
    if (!CategoryService.instance) {
      CategoryService.instance = new CategoryService();
    }
    return CategoryService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    CategoryService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return CategoryService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get categories with filtering, pagination and sorting
   * @param filters Filter criteria
   * @param pagination Pagination parameters
   * @returns Promise with paginated category list
   */
  public async getCategories(
    filters: CategoryFilters = {},
    pagination: CategoryPaginationParams = { currentPage: 1, pageSize: 20 }
  ): Promise<ApiResponse<PaginatedResponse<Category>>> {
    try {
      if (CategoryService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(300);

        // Import mock data dynamically to avoid circular dependencies
        const { getMockCategoryListResponse } = await import('../mock/categoryData');
        return getMockCategoryListResponse(filters, pagination);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.CATEGORY.PAGE, {
          ...filters,
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
          sortBy: pagination.sortBy,
          sortOrder: pagination.sortOrder,
        });
        return response.data;
      }
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      return createErrorResponse(error.message || '获取类目列表失败', 'SERVER_ERROR');
    }
  }

  async listCategories(): Promise<ApiResponse<Category[]>> {
    if (CategoryService.useMock && WHOLE_USE_MOCK) {
      const { getAllCategoryResponse } = await import('../mock/categoryData');
      return getAllCategoryResponse();
    } else {
      const res = await httpClient.get(API_ENDPOINTS.CATEGORY.LIST);
      return res.data;
    }
  }

  async listDivision(params?: { searchStr?: string }): Promise<ApiResponse<StringOption[]>> {
    if (CategoryService.useMock && WHOLE_USE_MOCK) {
      const { getAllDivisionResponse } = await import('../mock/categoryData');
      return getAllDivisionResponse(params);
    } else {
      const res = await httpClient.post(API_ENDPOINTS.CATEGORY.LIST_DIVISION, {
        searchStr: params?.searchStr
      });
      return res.data;
    }
  }
  
  // 添加更多下拉选项的方法
  async listGroup(params?: { divisionCode?: string; searchStr?: string }): Promise<ApiResponse<StringOption[]>> {
    if (CategoryService.useMock && WHOLE_USE_MOCK) {
      const { getAllGroupResponse } = await import('../mock/categoryData');
      return getAllGroupResponse(params); // 修复：传递参数
    } else {
      const res = await httpClient.post(API_ENDPOINTS.CATEGORY.LIST_GROUP, {
        divisionCode: params?.divisionCode,
        searchStr: params?.searchStr
      });
      return res.data;
    }
  }
  
  async listDepartment(params?: { divisionCode?: string; groupCode?: string; searchStr?: string }): Promise<ApiResponse<StringOption[]>> {
    if (CategoryService.useMock && WHOLE_USE_MOCK) {
      const { getAllDepartmentResponse } = await import('../mock/categoryData');
      return getAllDepartmentResponse(params); // 修复：传递参数
    } else {
      const res = await httpClient.post(API_ENDPOINTS.CATEGORY.LIST_DEPARTMENT, {
        divisionCode: params?.divisionCode,
        groupCode: params?.groupCode,
        searchStr: params?.searchStr
      });
      return res.data;
    }
  }
  
  async listClass(params?: { divisionCode?: string; groupCode?: string; departmentCode?: string; searchStr?: string }): Promise<ApiResponse<StringOption[]>> {
    if (CategoryService.useMock && WHOLE_USE_MOCK) {
      const { getAllClassResponse } = await import('../mock/categoryData');
      return getAllClassResponse(params); // 修复：传递参数
    } else {
      const res = await httpClient.post(API_ENDPOINTS.CATEGORY.LIST_CLASS, {
        divisionCode: params?.divisionCode,
        groupCode: params?.groupCode,
        departmentCode: params?.departmentCode,
        searchStr: params?.searchStr
      });
      return res.data;
    }
  }
  
  async listSubClass(params?: { divisionCode?: string; groupCode?: string; departmentCode?: string; classCode?: string; searchStr?: string }): Promise<ApiResponse<StringOption[]>> {
    if (CategoryService.useMock && WHOLE_USE_MOCK) {
      // 为mock数据创建一个简单的响应，实际项目中应该有完整的mock实现
      const mockSubClasses = [
        {"label": "绿色叶菜", "value": "SUB001"},
        {"label": "白菜", "value": "SUB002"},
        {"label": "萝卜", "value": "SUB003"},
        {"label": "香蕉", "value": "SUB004"},
        {"label": "苹果", "value": "SUB005"},
        {"label": "猪肉", "value": "SUB006"},
        {"label": "牛肉", "value": "SUB007"},
        {"label": "洗发水", "value": "SUB008"},
        {"label": "护发素", "value": "SUB009"},
        {"label": "洗衣液", "value": "SUB010"},
        {"label": "T恤", "value": "SUB011"},
        {"label": "牛仔裤", "value": "SUB012"},
        {"label": "胡萝卜", "value": "SUB013"},
        {"label": "洗洁精", "value": "SUB014"},
        {"label": "夏装", "value": "SUB015"},
        {"label": "冬装", "value": "SUB016"}
      ];
      
      // 简单的搜索过滤
      let filteredSubClasses = mockSubClasses;
      if (params?.searchStr) {
        const searchTerm = params.searchStr.toLowerCase();
        filteredSubClasses = filteredSubClasses.filter(subClass => 
          subClass.label.toLowerCase().includes(searchTerm) || 
          subClass.value.toLowerCase().includes(searchTerm)
        );
      }
      
      return createSuccessResponse(filteredSubClasses);
    } else {
      const res = await httpClient.post(API_ENDPOINTS.CATEGORY.LIST_SUB_CLASS, {
        divisionCode: params?.divisionCode,
        groupCode: params?.groupCode,
        departmentCode: params?.departmentCode,
        classCode: params?.classCode,
        searchStr: params?.searchStr
      });
      return res.data;
    }
  }
}

// Export singleton instance
const categoryService = CategoryService.getInstance();
export default categoryService;